import axios from 'axios';
import set from 'lodash/set';
import { getCookie } from 'sutils';
import { DOCUMENT_STATUS } from '@tencent/ark-model';
import { ISlideFileData, SlideFile } from '@tencent/slide-sdk-model';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';
import { IClientVars } from '@src/slide/domain/slide-app/core/interfaces/client-vars.interface';
import { createEmptyClientVars } from '@src/slide/domain/slide-app/core/factory/client-vars-factory';
import { IOfflineEditContext } from '../core/interfaces/offline-edit-context.interace';
import { waitForSlideAppReady } from '../utils';
import { DVER, INIT_MAX } from '../constants';
import omit from 'lodash/omit';
import { cloneDeep } from '@tencent/slide-sdk-model/src/utils/clone-deep';

/**
 * 3.0 json 数据转 RevisionBase64
 * @param jsonData
 * @returns
 */
function convertJsonDataToPresentation(slideFileData: ISlideFileData) {
    const serializedPresentationData = SlideFile
        .fromCommandData(new SlideFile(slideFileData).getCommandData()).getData();
    return serializedPresentationData.presentation;
}

/**
 * 创建带空数据模板的默认 ClientVars
 * @param context 包含文档基础参数
 */
export async function createDefaultClientVars(context: IOfflineEditContext): Promise<IClientVars> {
    const { uid, uin, secretId4New, globalPadId4New, title4New } = context;
    const { getDefaultTemplateData } = await import('@tencent/slide-sdk-preload/lib');
    const newSlideTemplateData = cloneDeep(getDefaultTemplateData());
    const domainId = typeof globalPadId4New === 'string' ? globalPadId4New.split('$')[0] : '';
    const padId = typeof globalPadId4New === 'string' ? globalPadId4New.split('$')[1] : '';
    const userId = `p.${uid}`;

    const newClientVars = createEmptyClientVars();
    newClientVars.padStatus = DOCUMENT_STATUS.new;
    newClientVars.globalPadId = globalPadId4New;
    newClientVars.domainId = domainId;
    newClientVars.creatorDomain = domainId;
    newClientVars.isOwner = true;
    newClientVars.title = title4New;
    newClientVars.padTitle = title4New;
    newClientVars.padId = padId;
    newClientVars.localPadId = padId;
    newClientVars.uin = uin;
    newClientVars.userId = userId;
    if (newClientVars?.collab_client_vars?.client) {
        newClientVars.collab_client_vars.client.upid = secretId4New;
    }
    if (newClientVars?.collab_client_vars?.initialAttributedText) {
        const presentation = convertJsonDataToPresentation(newSlideTemplateData) as string;
        newClientVars.collab_client_vars.initialAttributedText.text = presentation;
    }
    if (newClientVars.collab_client_vars) {
        newClientVars.collab_client_vars.globalPadId = globalPadId4New;
        newClientVars.collab_client_vars.dver = DVER;
        newClientVars.collab_client_vars.ver = INIT_MAX;
        newClientVars.collab_client_vars.padId = padId;
    }
    return newClientVars;
}

/**
 * 本地 clientVars 可能和远端返回 clientVars 部分字段不一致
 * 将远端/本地数据库拉取的 clientVars 同步至最新
 * @param clientVars
 * @returns
 */
export function syncClientVarsInfo(clientVars: IClientVars): IClientVars {
    const newClientVars = clientVars;
    const curUserInfo = window?.clientVars?.userInfo;
    if (curUserInfo?.uid) {
        newClientVars.userInfo = curUserInfo;
        (newClientVars as any).userName = curUserInfo.nick;
        (newClientVars as any).userPic = curUserInfo.avatar;
        newClientVars.userLoginCorpId = curUserInfo.corpId;
        newClientVars.userLoginCorpName = curUserInfo.corpName;
        newClientVars.vipInfo = curUserInfo.vipInfo;
    }
    newClientVars.padTitle = newClientVars.padTitle || newClientVars.title;
    newClientVars.padId = newClientVars.padId || newClientVars.collab_client_vars?.padId;
    return newClientVars;
}

/**
 * 非分页一次性全量获取数据(新建后主动获取或数据库中取数据)
 * 需 mock 非分页加载的 slide 参数
 * @param clientVars
 * @returns
 */
export function setClientVarsPaginated(clientVars: IClientVars): IClientVars {
    const newClientVars = clientVars;
    // 默认初始化数据即全量数据
    const slide = {
        count: null,
        isPaginated: false,
        startIndex: null,
        total: null,
    };
    set(newClientVars, 'collab_client_vars.initialAttributedText.slide', slide);
    return newClientVars;
}

/**
 * clientVars 变更同步
 * @param clientVars
 * 同步 window.clientVars 数据
 * 同步 ClientVarsService 内数据
 */
export function syncClientVars(clientVars: IClientVars) {
    (window as any).clientVars = clientVars;
    SlideDataServices.ClientVarsService.promise().then((clientVarsData) => {
        clientVarsData.setClientVars(clientVars);
    });
}

/**
 * 费时操作，调用方需要处理好高频调用的情况
 * 复写 slide-sdk 中获取当前 SlideApp 中 clientVars 方法
 * initialAttributedText.text 字段统一保留 presiontation 字段
 * 数据库存储 clientVars 均为带 presiontation 的不分页全量数据
 * @returns
 */
export async function createCurClientVars(slideFileData?: ISlideFileData) {
    // 确保全量数据加载完成 - 兼容在线打开首次获取带全量数据的 clientVars 场景
    await waitForSlideAppReady();
    const clientVars = await SlideDataServices.ClientVarsService.promise();
    const clientVarsData = clientVars.getClientVars();
    const presentationStoreService = await SlideDataServices.SDKDataServices.PresentationStoreService.promise();
    const collaborationService = await OnlineEditServices.CollaborationManager.promise();
    const presentationData = slideFileData ?? await presentationStoreService.getSerializedPresentationDataByWorker();
    const cloneClientVars = {
        ...omit(clientVarsData, 'collab_client_vars'),
        collab_client_vars: {
            ...omit(clientVarsData.collab_client_vars, 'initialAttributedText'),
            initialAttributedText: {
                ...omit(clientVarsData.collab_client_vars?.initialAttributedText, 'text'),
                text: JSON.stringify(presentationData),
            },
            rev: collaborationService.getRevision().getRevisionNumber(),
        },
    };
    return cloneClientVars as IClientVars;
}

/**
 * 复写 slide-sdk 中 fetchSlideData 方法，拉取文档数据
 * 不分页 - 直接请求全量数据
 * normal 参数不传会返回转义前的数据
 * @param secretId 文档id，链接/slide/xxx的xxx为id
 */
export function fetchSlideData(secretId: string) {
    const docsBaseUrl = (window as any)?.location?.hostname ? `//${(window as any).location.hostname}/` : '//docs.qq.com/';
    const xsrf = getCookie('TOK');
    return axios.get(`${docsBaseUrl}dop-api/slide/data/get?id=${secretId}&xsrf=${xsrf}&needPaginate=0&normal=1`);
}
